import { BakimliMusteri } from '../hooks/useBakimTakip';
import { bakimHesapla } from '../utils/bakimHesapla';
import { useNavigate } from 'react-router-dom';

interface Props {
  customer: BakimliMusteri;
}

const durumStyle: Record<string, {
  header: string;
  badge: string;
  progress: string;
  progressBg: string;
  border: string;
  icon: string;
  aciklamaColor: string;
}> = {
  yesil: {
    header: 'bg-emerald-500',
    badge: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    progress: 'bg-emerald-500',
    progressBg: 'bg-emerald-100',
    border: 'border-emerald-100',
    icon: 'ri-checkbox-circle-fill text-emerald-500',
    aciklamaColor: 'text-emerald-600',
  },
  sari: {
    header: 'bg-yellow-400',
    badge: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    progress: 'bg-yellow-400',
    progressBg: 'bg-yellow-100',
    border: 'border-yellow-100',
    icon: 'ri-time-fill text-yellow-500',
    aciklamaColor: 'text-yellow-600',
  },
  turuncu: {
    header: 'bg-orange-500',
    badge: 'bg-orange-100 text-orange-700 border-orange-200',
    progress: 'bg-orange-500',
    progressBg: 'bg-orange-100',
    border: 'border-orange-100',
    icon: 'ri-alert-fill text-orange-500',
    aciklamaColor: 'text-orange-600',
  },
  kirmizi: {
    header: 'bg-red-500',
    badge: 'bg-red-100 text-red-700 border-red-200',
    progress: 'bg-red-500',
    progressBg: 'bg-red-100',
    border: 'border-red-200',
    icon: 'ri-alarm-warning-fill text-red-500',
    aciklamaColor: 'text-red-600',
  },
};

const yokStil = {
  header: 'bg-gray-300',
  badge: 'bg-gray-100 text-gray-500 border-gray-200',
  progress: 'bg-gray-300',
  progressBg: 'bg-gray-100',
  border: 'border-gray-100',
  icon: 'ri-question-line text-gray-400',
  aciklamaColor: 'text-gray-400',
};

export default function BakimMusteriKartiSupabase({ customer }: Props) {
  const navigate = useNavigate();
  const bilgi = customer.son_bakim_tarihi ? bakimHesapla(customer.son_bakim_tarihi) : {
    ayFarki: 0, gunFarki: 0, durum: 'yesil' as const,
    etiket: 'Bakım Yok', aciklama: 'Henüz bakım kaydı yok', progressYuzde: 0,
    altiAyTarihi: '', dokuzAyTarihi: '', onikiAyTarihi: '',
  };
  const hasBakim = !!customer.son_bakim_tarihi;
  const stil = hasBakim ? (durumStyle[bilgi.durum] || durumStyle.yesil) : yokStil;

  const telefon = customer.telefon.replace(/\s/g, '').replace(/^0/, '90');
  const adSoyad = `${customer.ad} ${customer.soyad}`;
  const whatsappMsg = encodeURIComponent(`Sayın ${adSoyad}, su arıtma cihazınızın bakım süresi ${bilgi.etiket.toLowerCase()}. Servis için bizimle iletişime geçebilirsiniz. - Sadem Su Arıtma`);
  const whatsappUrl = `https://wa.me/${telefon}?text=${whatsappMsg}`;

  return (
    <div className={`bg-white rounded-xl border ${stil.border} overflow-hidden hover:shadow-sm transition-all duration-200`}>
      <div className={`h-2 w-full ${stil.header}`} />
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-800 truncate">{adSoyad}</p>
            <p className="text-xs text-gray-400 truncate">{customer.ilce}, {customer.il}</p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`${stil.icon} text-base`}></i>
            </div>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${stil.badge} whitespace-nowrap`}>{bilgi.etiket}</span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 mb-3">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-filter-3-line text-gray-400 text-sm"></i>
          </div>
          <span className="text-xs text-gray-500 truncate">{customer.cihaz_turu || 'Belirtilmemiş'}</span>
        </div>

        <div className="bg-slate-50 rounded-lg px-3 py-2 mb-3">
          <p className="text-xs text-gray-400 font-medium">Son Bakım Tarihi</p>
          <p className="text-sm font-bold text-gray-800 mt-0.5">
            {customer.son_bakim_tarihi ? new Date(customer.son_bakim_tarihi).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Kayıt yok'}
          </p>
          <p className={`text-xs font-semibold mt-0.5 ${stil.aciklamaColor}`}>{bilgi.aciklama}</p>
        </div>

        {hasBakim && (
          <div className="mb-4">
            <div className="grid grid-cols-3 gap-1 text-[10px] text-gray-400 mb-3">
              <div>
                <p className="font-medium text-gray-500">6. Ay</p>
                <p>{bilgi.altiAyTarihi ? new Date(bilgi.altiAyTarihi).toLocaleDateString('tr-TR') : '-'}</p>
              </div>
              <div>
                <p className="font-medium text-gray-500">9. Ay</p>
                <p>{bilgi.dokuzAyTarihi ? new Date(bilgi.dokuzAyTarihi).toLocaleDateString('tr-TR') : '-'}</p>
              </div>
              <div>
                <p className="font-medium text-gray-500">12. Ay</p>
                <p>{bilgi.onikiAyTarihi ? new Date(bilgi.onikiAyTarihi).toLocaleDateString('tr-TR') : '-'}</p>
              </div>
            </div>
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>0 ay</span>
              <span className="text-gray-500 font-medium">{Math.floor(bilgi.ayFarki)} ay geçti</span>
              <span>12 ay</span>
            </div>
            <div className={`h-2 rounded-full ${stil.progressBg}`}>
              <div className={`h-2 rounded-full transition-all duration-500 ${stil.progress}`} style={{ width: `${bilgi.progressYuzde}%` }} />
            </div>
            <div className="flex justify-between text-xs text-gray-300 mt-1">
              <span></span>
              <span className="text-yellow-500 font-medium">|{6}ay</span>
              <span className="text-orange-500 font-medium">|{9}ay</span>
              <span className="text-red-400 font-medium">|{12}ay</span>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <a href={`tel:${customer.telefon}`} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-phone-line"></i><span>Ara</span>
          </a>
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-whatsapp-line"></i><span>WhatsApp</span>
          </a>
          <button onClick={() => navigate('/servis-kayitlari')} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-sky-50 hover:bg-sky-100 text-sky-700 text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-calendar-check-line"></i><span>Servis</span>
          </button>
        </div>
      </div>
    </div>
  );
}